import { BullhornDataSourceApiService, BullhornEntityTypes } from 'datasource-service-lib';
import { BullhornEntityMetadataService } from 'entity-metadata-service-lib';
import { IEntity, IEntityMetadata, INamedEntity } from 'interfaces-lib';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { runtimeEnvironment } from '../../../../environments/environment';
import {
  ISimpleTableColumn,
  ISimpleTableRow,
  SimpleTableSortFunctions,
} from '../../../common-components/simple-table/simple-table.interfaces';
import { IEntitySelectDialogService } from './ientityselectdialogservice';

type EntityInfo = INamedEntity & { status: string };

export class BullhornEntitySelectDialogService implements IEntitySelectDialogService {
  entitiesMeta: IEntityMetadata[];

  constructor(private dataSourceApiService: BullhornDataSourceApiService, private entityMetaService: BullhornEntityMetadataService) {
    console.debug('BullhornEntitySelectDialogService created.');
    this.entityMetaService.getEntitiesMetadata().subscribe((entities) => (this.entitiesMeta = entities));
  }

  getDisplayTerms(entity: IEntity): {
    listEntityType: string;
    listEntityLabel: string;
  } {
    const candidatesLabel = this.entitiesMeta.find((e) => e.name === BullhornEntityTypes.Candidate)?.label;
    const contactsLabel = this.entitiesMeta.find((e) => e.name === BullhornEntityTypes.ClientContact)?.label;
    switch (entity.type) {
      case BullhornEntityTypes.JobOrder:
        return {
          listEntityLabel: candidatesLabel,
          listEntityType: BullhornEntityTypes.Candidate,
        };
      case BullhornEntityTypes.ClientCorporation:
        return {
          listEntityLabel: contactsLabel,
          listEntityType: BullhornEntityTypes.ClientContact,
        };
      default:
        throw new Error(`Unexpected entity type '${entity.type}'.`);
    }
  }

  getDisplayColumns(): ISimpleTableColumn[] {
    if (runtimeEnvironment.renderMode === 'web-app') {
      return [
        {
          title: 'ID',
          width: '18%',
          sortFunction: SimpleTableSortFunctions.sortByTextField('id'),
        },
        {
          title: 'Name',
          width: '41%',
          sortFunction: SimpleTableSortFunctions.sortByTextField('name'),
        },
        {
          title: 'Status',
          width: '41%',
          sortFunction: SimpleTableSortFunctions.sortByTextField('status'),
        },
      ];
    }

    // chrome-extension
    return [
      {
        title: 'ID',
        width: '18%',
        sortFunction: SimpleTableSortFunctions.sortByTextField('id'),
      },
      {
        title: 'Name',
        width: '41%',
        sortFunction: SimpleTableSortFunctions.sortByTextField('name'),
      },
      {
        title: 'Status',
        width: '41%',
        sortFunction: SimpleTableSortFunctions.sortByTextField('status'),
      },
    ];
  }

  getRows(entity: IEntity): Observable<ISimpleTableRow[]> {
    console.debug(`Bullhorn: getRows(${entity.id}, ${entity.type})`);
    switch (entity.type) {
      case BullhornEntityTypes.JobOrder:
        return this.getCandidatesForJobOrder(entity).pipe(
          map((entityInfoArray) => {
            return entityInfoArray.map((entityInfo) => this.buildTableRow(entityInfo));
          })
        );
      case BullhornEntityTypes.ClientCorporation:
        return this.getContactsForClientCorporation(entity).pipe(
          map((entityInfoArray) => {
            return entityInfoArray.map((entityInfo) => this.buildTableRow(entityInfo));
          })
        );
      default:
        throw new Error(`Unexpected entity type '${entity.type}'.`);
    }
  }

  private getContactsForClientCorporation(clientCorporation: IEntity): Observable<EntityInfo[]> {
    const entity = { entity: { type: BullhornEntityTypes.ClientContact } };
    const query = `clientCorporation.id:${clientCorporation.id} AND isDeleted:false AND -status:Archive`;
    const fields = 'id,firstName,lastName,status';
    return this.dataSourceApiService.list2({ ...entity, query, fields }).pipe(
      map((contacts) => {
        return contacts.map((contact) => {
          return {
            id: contact.id.toString(),
            type: BullhornEntityTypes.ClientContact,
            name: `${contact.firstName} ${contact.lastName}`,
            status: contact.status,
          };
        });
      })
    );
  }

  private getCandidatesForJobOrder(jobOrder: IEntity): Observable<EntityInfo[]> {
    const jobSubmission = {
      entity: { type: BullhornEntityTypes.JobSubmission },
    };
    const query =
      `jobOrder.id:${jobOrder.id} AND isDeleted:false AND candidate.isDeleted:false` +
      ' AND -candidate.status:Archive AND -status:Archive AND -status:"New Lead"';
    const fields = 'status, candidate(id,firstName,lastName)';

    return this.dataSourceApiService.list2({ ...jobSubmission, query, fields }).pipe(
      map((candidates) => {
        return candidates.map((record) => {
          const candidate = record.candidate;
          return {
            id: candidate.id.toString(),
            type: BullhornEntityTypes.Candidate,
            name: `${candidate.firstName} ${candidate.lastName}`,
            status: record.status,
          };
        });
      })
    );
  }

  private buildTableRow(entityInfo: EntityInfo): ISimpleTableRow {
    const cells = [{ text: entityInfo.id }, { text: entityInfo.name }, { text: entityInfo.status }];
    return { cells: cells, data: entityInfo };
  }
}
